import {
  Grid,
  Stack,
  TextField,
  Typography,
  Box,
  Button,
  Checkbox,
} from "@mui/material";
import FactoryDrag from "./FactoryDrag";
import { useApp } from "../../../../hooks/useApp";
import Autocomplete from "@mui/material/Autocomplete";
import { useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Tooltip from "@mui/material/Tooltip";
import { SketchPicker } from "react-color";
import ClickAwayListener from "@mui/material/ClickAwayListener";

export default function FactoryFour({ asset, setAsset }) {
  const { aiFeatures } = useApp("ai_features");
  const [dropDown, setDropDown] = useState(null);
  const [openPickerIndex, setOpenPickerIndex] = useState(null);

  const ensureChildrenArray = (currentAsset) => {
    return {
      ...currentAsset,
      AiFeatures: Array.isArray(currentAsset?.AiFeatures)
        ? currentAsset?.AiFeatures
        : [],
    };
  };

  function handleAdd() {
    const updatedAsset = ensureChildrenArray(asset);

    if (!dropDown) {
      alert("Please select an asset before adding.");
      return;
    }

    if (updatedAsset?.AiFeatures?.some((child) => child.id === dropDown.id)) {
      alert("This asset is already added.");
      return;
    }

    const newChild = {
      id: dropDown.id,
      Title: dropDown.Title,
      Left: "50%",
      Top: "50%",
      flow: false,
      color: "#b0e0e6",
    };

    setAsset((prevAsset) => ({
        ...ensureChildrenArray(prevAsset),
        AiFeatures: [...ensureChildrenArray(prevAsset)?.AiFeatures, newChild],
    }));

    setDropDown(null);
  }

  const moveAsset = (index, direction) => {
    const updatedAsset = ensureChildrenArray(asset);
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < updatedAsset?.AiFeatures?.length) {
      const newChildren = [...updatedAsset?.AiFeatures];
      const [movedItem] = newChildren.splice(index, 1);
      newChildren.splice(newIndex, 0, movedItem);
      setAsset({ ...updatedAsset, AiFeatures: newChildren });
    }
  };

  const handleChildColorChange = (index, color) => {
    setAsset((prev) => {
      const updatedPrev = ensureChildrenArray(prev);

      const newChildren = updatedPrev?.AiFeatures?.map((child, i) =>
        i === index ? { ...child, color } : child
      );

      const result = { ...updatedPrev, AiFeatures: newChildren };

      return result;
    });
  };

  return (
    <Grid container sx={{ flexDirection: "row", gap: 4 }}>
      <Grid
        item
        sx={{
          justifyContent: "center",
          alignItems: "center",
          width: "fit-content",
        }}
        xs={12}
        xl={8}
      >
        <FactoryDrag asset={asset} setAsset={setAsset} />
      </Grid>

      <Grid item xs>
        <Stack direction={"column"} gap={1} sx={{ height: "100%" }}>
          <Typography sx={{ mt: 2 }}>AI Features</Typography>
          <Stack direction={"row"}>
            {aiFeatures && (
              <Autocomplete
                disablePortal
                id="aiFeatureSelect"
                options={aiFeatures}
                getOptionLabel={(option) => option.Title || ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                sx={{ width: "100%" }}
                size="small"
                value={dropDown}
                onChange={(event, newValue) => {
                  setDropDown(newValue);
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id || option.Title}>
                    {option.Title || ""}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Title" />
                )}
              />
            )}
            <Button onClick={handleAdd} >Add</Button>
          </Stack>

          <Box
            sx={{
              border: "1px solid #BDBDBD",
              height: "100%",
              paddingBlock: 2,
              paddingInline: 1,
              borderRadius: 2,
            }}
          >
            {asset &&
              asset?.AiFeatures?.length > 0 &&
              asset.AiFeatures.map((child, thisIndex) => {
                const isOpen = openPickerIndex === thisIndex;
                return (
                  <Stack
                    key={thisIndex}
                    direction={"column"}
                    spacing={1}
                    marginBottom={2}
                    borderBottom={1}
                    borderColor={"#BDBDBD"}
                    pb={1}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography>{child.Title}</Typography>
                      <div className="flex align-items-center">
                        {child.flow && (
                          <>
                            <IconButton
                              aria-label="down"
                              size="small"
                              onClick={() => moveAsset(thisIndex, "down")}
                              disabled={
                                thisIndex === asset.AiFeatures.length - 1
                              }
                            >
                              <ArrowDownwardIcon fontSize={"small"} />
                            </IconButton>
                            <IconButton
                              aria-label="up"
                              size="small"
                              onClick={() => moveAsset(thisIndex, "up")}
                              disabled={thisIndex === 0}
                            >
                              <ArrowUpwardIcon fontSize={"small"} />
                            </IconButton>
                          </>
                        )}
                        <Box
                          onClick={() =>
                            setOpenPickerIndex(isOpen ? null : thisIndex)
                          }
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "4px",
                            backgroundColor: child.color || "#b0e0e6",
                            border: "1px solid #ccc",
                            cursor: "pointer",
                            mr: 1,
                          }}
                        />
                        <Tooltip title="Flow" placement="left">
                          <Checkbox
                            checked={child.flow || false}
                            name="flow"
                            onClick={(e) => {
                              setAsset((prevAsset) => {
                                const updatedPrev =
                                  ensureChildrenArray(prevAsset);
                                const newSuites = [...updatedPrev.AiFeatures];
                                newSuites[thisIndex] = {
                                  ...newSuites[thisIndex],
                                  flow: e.target.checked,
                                };
                                return {
                                  ...updatedPrev,
                                  AiFeatures: newSuites,
                                };
                              });
                            }}
                          />
                        </Tooltip>
                        <IconButton
                          aria-label="delete"
                          onClick={() => {
                            const newChildren = asset.AiFeatures.filter(
                              (_, index) => index !== thisIndex
                            );
                            setAsset({ ...asset, AiFeatures: newChildren });
                          }}
                        >
                          <DeleteIcon fontSize={"small"} />
                        </IconButton>
                      </div>
                    </Stack>

                    {isOpen && (
                      <ClickAwayListener
                        onClickAway={() => setOpenPickerIndex(null)}
                      >
                        <Box className="color-picker">
                          <SketchPicker
                            color={child.color || "#b0e0e6"}
                            onChangeComplete={(color) =>
                              handleChildColorChange(thisIndex, color.hex)
                            }
                          />
                        </Box>
                      </ClickAwayListener>
                    )}
                  </Stack>
                );
              })}
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
}
